import { useMemo } from 'react';
import { useAlert } from 'react-alert';
import { useForm } from 'react-hook-form';
import { FormState } from 'react-hook-form/dist/types/form';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import I18n, { t } from 'i18n-js';
import * as yup from 'yup';

// @ts-ignore lib needs types
import { sendIssueEmailToSupport } from '@lootboy/lib/api/users';
import { CONTACT_VIEW_CONSTRAINS } from '@lootboy/lib/constants';
import fieldValidationMessageIds from '@lootboy/lib/forms/validationMessages';
import {
  authSelector,
  emailSelector,
  userSelector,
} from '@lootboy/lib/selectors';

import Alert from '~/components/Alert';
import { TUseFormReturnWrapped } from '~/components/atoms/FormControls/Form';

import {
  CONTACT_TOPICS,
  EContactFormField,
  EContactFormTopic,
  TContactFormFieldValues,
} from '~/popups/ContactPopup/consts';
import { notTranslated } from '~/utilities/devTools';

const { required, invalidEmail } = fieldValidationMessageIds;
type TSubmittingEnabled = <T>(formState: FormState<T>) => boolean;
const isSubmittingEnabled: TSubmittingEnabled = ({
  isSubmitting,
  isSubmitSuccessful,
  isLoading,
  isValidating,
}) => !isSubmitting && !isSubmitSuccessful && !isLoading && !isValidating;

// todo is restriction on file size needed?
export const contactFormSchema = yup.object({
  [EContactFormField.EMAIL]: yup
    .string()
    .required(required)
    .email(invalidEmail)
    .max(CONTACT_VIEW_CONSTRAINS.MAX_EMAIL_LENGTH, notTranslated('too long')),
  [EContactFormField.USERNAME]: yup.string().required(required),
  [EContactFormField.TOPIC]: yup.string().required(required),
  [EContactFormField.MESSAGE]: yup
    .string()
    .required(required)
    .max(CONTACT_VIEW_CONSTRAINS.MAX_TEXT_LENGTH, notTranslated('too long')),
  [EContactFormField.ADDITIONAL_INFO]: yup
    .string()
    .when(EContactFormField.TOPIC, {
      is: (topic: EContactFormTopic) => CONTACT_TOPICS[topic]?.additionalField,
      then: (schema) => schema.required(required),
    })
    .max(CONTACT_VIEW_CONSTRAINS.MAX_TEXT_LENGTH, notTranslated('too long')),

  [EContactFormField.SCREENSHOTS]: yup
    .array()
    .max(CONTACT_VIEW_CONSTRAINS.MAX_ATTACHMENT_COUNT)
    .when(EContactFormField.TOPIC, {
      is: (topic: EContactFormTopic) =>
        CONTACT_TOPICS[topic]?.attachmentMandatory,
      then: (schema) => schema.required(required),
    }),
});

export function useContactForm(): TUseFormReturnWrapped<TContactFormFieldValues> & {
  userNameField: boolean;
} {
  const alert = useAlert();
  const history = useHistory();
  // @ts-ignore lib needs types
  const { apiUrl } = useSelector(authSelector);
  const { username = '' }: { username?: string } = useSelector(userSelector);
  const userEmail: string | undefined = useSelector(emailSelector);
  const form = useForm<TContactFormFieldValues>({
    defaultValues: {
      [EContactFormField.EMAIL]: userEmail,
      [EContactFormField.USERNAME]:
        username || I18n.t('ContactView_label_name_not_logged_in'),
    },
    resolver: yupResolver(contactFormSchema),
  });
  const { handleSubmit, formState } = form;

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(
        async ({
          [EContactFormField.TOPIC]: topic,
          [EContactFormField.SCREENSHOTS]: screenshots,
          [EContactFormField.MESSAGE]: message,
          [EContactFormField.ADDITIONAL_INFO]: additionaInfo,
          ...values
        }) => {
          try {
            let text = message;
            const additional = CONTACT_TOPICS[topic].additionalField;
            if (additional && additionaInfo) {
              text += `\n\n${t(additional.label)}\n${additionaInfo}`;
            }
            await sendIssueEmailToSupport(
              apiUrl,
              {
                ...values,
                [EContactFormField.TOPIC]: t(topic),
                text,
              },
              screenshots || [],
            );
            alert.success(
              <Alert
                body={I18n.t('ContactView_successful_submit')}
                title={I18n.t('common_success')}
              />,
            );
            history.goBack();
          } catch (error) {
            alert.error(
              <Alert
                body={(error as Error).message}
                title={I18n.t('common_error')}
              />,
            );
          }
        },
      ),
    [history, alert, apiUrl, handleSubmit],
  );

  return {
    ...form,
    isSubmitEnabled: isSubmittingEnabled(formState),
    userNameField: Boolean(username),
    onFormSubmit,
  };
}
